<template>
  <div>
    <!-- Mobile overlay -->
    <div
      v-if="isOpen && isMobile"
      class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
      @click="$emit('close')"
    ></div>

    <!-- Sidebar -->
    <div
      class="fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0"
      :class="{
        'translate-x-0': isOpen,
        '-translate-x-full': !isOpen
      }"
    >
      <!-- Logo -->
      <div class="flex items-center justify-center h-16 px-4 bg-primary-600 dark:bg-primary-700">
        <div class="flex items-center space-x-2">
          <div class="h-8 w-8 bg-white rounded-lg flex items-center justify-center">
            <svg class="h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <span class="text-white font-bold text-lg">EduSys</span>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="mt-5 px-2 space-y-1 h-full overflow-y-auto pb-20">
        <!-- User Info -->
        <div class="px-3 py-4 border-b border-gray-200 dark:border-gray-700 mb-4">
          <div class="flex items-center space-x-3">
            <div class="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              <span class="text-primary-600 dark:text-primary-400 font-medium text-sm">
                {{ userInitials }}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                {{ authStore.user?.full_name || 'Usuário' }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
                {{ roleLabel }}
              </p>
            </div>
          </div>
        </div>

        <!-- Menu Items -->
        <template v-for="item in menuItems" :key="item.name">
          <!-- Single Menu Item -->
          <router-link
            v-if="!item.children"
            :to="item.path"
            class="group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
            :class="{
              'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300': $route.path === item.path,
              'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700': $route.path !== item.path
            }"
            @click="isMobile && $emit('close')"
          >
            <component
              :is="getIcon(item.icon)"
              class="mr-3 h-5 w-5 flex-shrink-0"
              :class="{
                'text-primary-600 dark:text-primary-400': $route.path === item.path,
                'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300': $route.path !== item.path
              }"
            />
            {{ item.name }}
          </router-link>

          <!-- Menu Item with Children -->
          <div v-else>
            <button
              @click="toggleSubmenu(item.name)"
              class="group w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <div class="flex items-center">
                <component
                  :is="getIcon(item.icon)"
                  class="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300"
                />
                {{ item.name }}
              </div>
              <ChevronDownIcon
                class="h-4 w-4 transition-transform duration-200"
                :class="{
                  'transform rotate-180': openSubmenus.includes(item.name)
                }"
              />
            </button>

            <!-- Submenu -->
            <div
              v-show="openSubmenus.includes(item.name)"
              class="mt-1 space-y-1"
            >
              <router-link
                v-for="child in item.children"
                :key="child.name"
                :to="child.path"
                class="group flex items-center pl-11 pr-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
                :class="{
                  'bg-primary-50 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300': $route.path === child.path,
                  'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50': $route.path !== child.path
                }"
                @click="isMobile && $emit('close')"
              >
                <component
                  :is="getIcon(child.icon)"
                  class="mr-3 h-4 w-4 flex-shrink-0"
                  :class="{
                    'text-primary-600 dark:text-primary-400': $route.path === child.path,
                    'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300': $route.path !== child.path
                  }"
                />
                {{ child.name }}
              </router-link>
            </div>
          </div>
        </template>

        <!-- Divider -->
        <div class="border-t border-gray-200 dark:border-gray-700 my-4"></div>

        <!-- Settings and Logout -->
        <router-link
          to="/settings"
          class="group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
          :class="{
            'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300': $route.path === '/settings',
            'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700': $route.path !== '/settings'
          }"
          @click="isMobile && $emit('close')"
        >
          <CogIcon
            class="mr-3 h-5 w-5 flex-shrink-0"
            :class="{
              'text-primary-600 dark:text-primary-400': $route.path === '/settings',
              'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300': $route.path !== '/settings'
            }"
          />
          Configurações
        </router-link>

        <button
          @click="handleLogout"
          class="group w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-400 transition-colors duration-200"
        >
          <ArrowRightOnRectangleIcon
            class="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-red-500"
          />
          Sair
        </button>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { getMenuItems } from '@/router';
import {
  ChevronDownIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  AcademicCapIcon,
  UserGroupIcon,
  BookOpenIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ReceiptPercentIcon, 
  CreditCardIcon,
  ChartPieIcon,
  ChatBubbleLeftRightIcon,
  BellIcon,  
  UsersIcon,
  AdjustmentsHorizontalIcon,
  ChartBarIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  FolderIcon,
  DocumentPlusIcon,
  DocumentTextIcon,
} from '@heroicons/vue/24/outline';

// Props
interface Props {
  isOpen: boolean;
}

defineProps<Props>();

// Emits
defineEmits<{
  close: [];
}>();

// Stores
const authStore = useAuthStore();
const router = useRouter();

// State
const openSubmenus = ref<string[]>([]);
const isMobile = ref(false);

// Icon mapping
const iconMap = {
  home: HomeIcon,
  'academic-cap': AcademicCapIcon,
  'user-group': UserGroupIcon,
  'book-open': BookOpenIcon,
  'clipboard-list': ClipboardDocumentListIcon,
  'check-circle': CheckCircleIcon,
  calendar: CalendarIcon,
  'currency-dollar': CurrencyDollarIcon,
  'receipt-tax': ReceiptPercentIcon,
  'credit-card': CreditCardIcon,
  'chart-pie': ChartPieIcon,
  chat: ChatBubbleLeftRightIcon,
  bell: BellIcon,
  speakerphone: BellIcon,
  cog: CogIcon,
  users: UsersIcon,
  adjustments: AdjustmentsHorizontalIcon,
  'chart-bar': ChartBarIcon,
  'user-circle': UserCircleIcon,
  user: UserCircleIcon,
  'building-office': BuildingOfficeIcon,
  folder: FolderIcon,
  'document-plus': DocumentPlusIcon,
  'document-text': DocumentTextIcon,
};

// Computed
const userInitials = computed(() => {
  const name = authStore.user?.full_name || 'U';
  return name
    .split(' ')
    .map((word: string) => word.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
});

const roleLabel = computed(() => {
  const roleLabels = {
    admin: 'Administrador',
    director: 'Diretor',
    coordinator: 'Coordenador',
    teacher: 'Professor',
    student: 'Aluno',
    parent: 'Responsável',
    financial: 'Financeiro',
  };
  return roleLabels[authStore.user?.role as keyof typeof roleLabels] || 'Usuário';
});

const menuItems = computed(() => {
  if (!authStore.user?.role) return [];
  return getMenuItems(authStore.user.role);
});

// Methods
const getIcon = (iconName: string) => {
  return iconMap[iconName as keyof typeof iconMap] || HomeIcon;
};

const toggleSubmenu = (menuName: string) => {
  const index = openSubmenus.value.indexOf(menuName);
  if (index > -1) {
    openSubmenus.value.splice(index, 1);
  } else {
    openSubmenus.value.push(menuName);
  }
};

const handleLogout = async () => {
  try {
    await authStore.logout();
    router.push('/auth/login');
  } catch (error) {
    console.error('Erro ao fazer logout:', error);
  }
};

const checkMobile = () => {
  isMobile.value = window.innerWidth < 1024;
};

// Lifecycle
onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
  
  // Open default submenus based on current route
  const currentPath = router.currentRoute.value.path;
  menuItems.value.forEach(item => {
    if (item.children) {
      const hasActiveChild = item.children.some(child => currentPath.startsWith(child.path));
      if (hasActiveChild && !openSubmenus.value.includes(item.name)) {
        openSubmenus.value.push(item.name);
      }
    }
  });
});
</script>

<style scoped>
/* Custom scrollbar for navigation */
nav {
  scrollbar-width: thin;
  scrollbar-color: rgb(203 213 225) transparent;
}

nav::-webkit-scrollbar {
  width: 4px;
}

nav::-webkit-scrollbar-track {
  background: transparent;
}

nav::-webkit-scrollbar-thumb {
  background-color: rgb(203 213 225);
  border-radius: 2px;
}

nav::-webkit-scrollbar-thumb:hover {
  background-color: rgb(148 163 184);
}

.dark nav::-webkit-scrollbar-thumb {
  background-color: rgb(71 85 105);
}

.dark nav::-webkit-scrollbar-thumb:hover {
  background-color: rgb(100 116 139);
}

/* Active link indicator */
.router-link-active {
  position: relative;
}

.router-link-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: rgb(37 99 235);
  border-radius: 0 2px 2px 0;
}

/* Smooth transitions */
.transition-transform {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* Focus styles for accessibility */
button:focus,
a:focus {
  outline: 2px solid rgb(59 130 246);
  outline-offset: 2px;
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .bg-white {
    border: 1px solid black;
  }
  
  .dark .bg-gray-800 {
    border: 1px solid white;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .transition-transform,
  .transition-colors {
    transition: none;
  }
}
</style>